import type { QueryClient } from '@tanstack/react-query';
import { dataIndexKeys, type AttributeScope, type DataIndexAttribute, type DataIndexEvent } from './entity';

/**
 * Cache writes for the data index.
 *
 * The metadata and tag endpoints answer 202 with no body, so a save has
 * nothing to write back: the detail slot is patched from what was sent, which
 * also leaves every derived field the response never carried (usage lists,
 * counts, sources) exactly as it was. The lists, totals and usage reads are
 * invalidated instead of patched, because a row can leave the current page
 * entirely once its tags or in-use state change — but the detail slot itself
 * is left alone, so the row the person is looking at does not blank while the
 * list catches up.
 */

/**
 * The metadata a save can change, carrying only the fields it actually sent.
 * A key present with an undefined value would overwrite the stored one: the
 * response returns nothing, so what is not sent must not be patched.
 */
export type MetadataPatch = {
  description?: string | null;
  privacy_level?: number;
  tag_ids?: number[];
};

/** Patch one attribute's metadata in place, leaving its usage and sources alone. */
export function setDataIndexAttributeMetadataCache(
  queryClient: QueryClient,
  workspaceId: number,
  scope: AttributeScope,
  name: string,
  patch: MetadataPatch,
): void {
  queryClient.setQueryData<DataIndexAttribute>(dataIndexKeys.attributeDetail(workspaceId, scope, name), (current) =>
    current == null ? current : { ...current, metadata: { ...current.metadata, ...patch } },
  );
}

/** Patch one event's metadata in place. Events carry no privacy level. */
export function setDataIndexEventMetadataCache(
  queryClient: QueryClient,
  workspaceId: number,
  name: string,
  patch: Omit<MetadataPatch, 'privacy_level'>,
): void {
  queryClient.setQueryData<DataIndexEvent>(dataIndexKeys.eventDetail(workspaceId, name), (current) =>
    current == null ? current : { ...current, metadata: { ...current.metadata, ...patch } },
  );
}

/** Drop a deleted attribute's detail slot. */
export function removeDataIndexAttributeCache(
  queryClient: QueryClient,
  workspaceId: number,
  scope: AttributeScope,
  name: string,
): void {
  queryClient.removeQueries({ queryKey: dataIndexKeys.attributeDetail(workspaceId, scope, name) });
}

/** Refetch every attribute list, total and usage read in the workspace, but not the details. */
export async function invalidateDataIndexAttributes(queryClient: QueryClient, workspaceId: number): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: dataIndexKeys.attributeLists(workspaceId) }),
    queryClient.invalidateQueries({ queryKey: dataIndexKeys.attributeTotalsAll(workspaceId) }),
    queryClient.invalidateQueries({ queryKey: dataIndexKeys.attributeUsageAll(workspaceId) }),
  ]);
}

/** Refetch every event list, total and usage read in the workspace, but not the details. */
export async function invalidateDataIndexEvents(queryClient: QueryClient, workspaceId: number): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: dataIndexKeys.eventLists(workspaceId) }),
    queryClient.invalidateQueries({ queryKey: dataIndexKeys.eventTotalsAll(workspaceId) }),
    queryClient.invalidateQueries({ queryKey: dataIndexKeys.eventUsageAll(workspaceId) }),
  ]);
}
